import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, MenuItem, Checkbox, FormControl, InputLabel, Select, ListItemText, OutlinedInput, CircularProgress, Chip, Popover, List, ListItemButton, TableSortLabel, Card, CardContent, useMediaQuery, useTheme, Link } from '@mui/material';
import { Refresh as RefreshIcon, ViewColumn as ColumnsIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const datePresets = [
  { label: 'Today', getValue: () => { const t = new Date(); return { start: t, end: t }; } },
  { label: 'Yesterday', getValue: () => { const t = new Date(); t.setDate(t.getDate() - 1); return { start: t, end: t }; } },
  { label: 'Last 7 Days', getValue: () => { const t = new Date(); const start = new Date(t); start.setDate(t.getDate() - 6); return { start, end: t }; } },
  { label: 'Last 30 Days', getValue: () => { const t = new Date(); const start = new Date(t); start.setDate(t.getDate() - 29); return { start, end: t }; } },
  { label: 'This Month', getValue: () => { const t = new Date(); const start = new Date(t.getFullYear(), t.getMonth(), 1); return { start, end: t }; } },
  { label: 'Last Month', getValue: () => { const t = new Date(); const start = new Date(t.getFullYear(), t.getMonth() - 1, 1); const end = new Date(t.getFullYear(), t.getMonth(), 0); return { start, end }; } },
];

const groupingColumns = [
  { group: 'Account', columns: ['campaign', 'partner', 'advertiser'] },
  { group: 'Time', columns: ['date'] },
];

const metricsColumns = ['gross_clicks', 'clicks', 'unique_clicks', 'total_cv', 'events', 'revenue', 'payout', 'profit', 'margin'];

const columnLabels = {
  campaign: 'Campaign', partner: 'Partner', advertiser: 'Advertiser', date: 'Date',
  gross_clicks: 'Gross Clicks', clicks: 'Clicks', unique_clicks: 'Uniq. Clicks',
  total_cv: 'Total CV', events: 'Events', revenue: 'Revenue', payout: 'Payout', profit: 'Profit', margin: 'Margin %'
};

const columnLabelsShort = {
  campaign: 'Camp.', partner: 'Partner', advertiser: 'Adv.', date: 'Date',
  gross_clicks: 'G.Clicks', clicks: 'Clicks', unique_clicks: 'U.Clicks',
  total_cv: 'CV', events: 'Events', revenue: 'Rev', payout: 'Pay', profit: 'Profit', margin: 'Margin'
};

function Analytics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isPartner } = useAuth();
  const isPartnerRole = isPartner();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedGrouping, setSelectedGrouping] = useState(isPartnerRole ? ['campaign'] : ['campaign', 'partner']);
  const [filters, setFilters] = useState({ campaigns: [], partners: [], advertisers: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [dateAnchor, setDateAnchor] = useState(null);
  const [colAnchor, setColAnchor] = useState(null);
  const [searchCampaign, setSearchCampaign] = useState('');
  const [searchPartner, setSearchPartner] = useState('');
  const [searchAdvertiser, setSearchAdvertiser] = useState('');
  const [orderBy, setOrderBy] = useState('revenue');
  const [orderDir, setOrderDir] = useState('desc');

  useEffect(() => { loadFilters(); }, []);

  const loadFilters = async () => {
    try {
      const [campRes, partRes, advRes] = await Promise.all([
        axios.get('/admin/campaigns'), 
        axios.get('/admin/partners'),
        axios.get('/admin/advertisers').catch(() => ({ data: [] }))
      ]);
      setCampaigns(Array.isArray(campRes.data) ? campRes.data : campRes.data?.data || []);
      setPartners(Array.isArray(partRes.data) ? partRes.data : partRes.data?.data || []);
      setAdvertisers(Array.isArray(advRes.data) ? advRes.data : advRes.data?.data || []);
    } catch (e) { console.error(e); }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
       startDate: `${startDate.getFullYear()}-${String(startDate.getMonth()+1).padStart(2,'0')}-${String(startDate.getDate()).padStart(2,'0')}`, 
        endDate: `${endDate.getFullYear()}-${String(endDate.getMonth()+1).padStart(2,'0')}-${String(endDate.getDate()).padStart(2,'0')}`,
        groupBy: selectedGrouping.join(',') 
      });
      if (filters.campaigns.length) params.append('campaigns', filters.campaigns.join(','));
      if (filters.partners.length && !isPartnerRole) params.append('partners', filters.partners.join(','));
      if (filters.advertisers.length && !isPartnerRole) params.append('advertisers', filters.advertisers.join(','));
      const res = await axios.get('/admin/analytics?' + params);
      setData(res.data.rows || []); 
      setSummary(res.data.summary || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const applyPreset = (preset) => {
    const { start, end } = preset.getValue();
    setStartDate(start);
    setEndDate(end);
    setDateAnchor(null);
  };

  const toggleGrouping = (col) => {
    if (isPartnerRole && (col === 'partner' || col === 'advertiser')) return;
    setSelectedGrouping(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDir('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (metricsColumns.includes(orderBy)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else {
      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();
    }
    if (orderDir === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const fmt = (col, val) => {
    if (val == null) return '-';
    if (['revenue', 'payout', 'profit'].includes(col)) return '€' + Number(val).toFixed(2);
    if (col === 'margin') return Number(val).toFixed(2) + '%';
    return Number(val).toLocaleString();
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) return 'Select dates';
    const s = startDate instanceof Date ? startDate : new Date(startDate);
    const e = endDate instanceof Date ? endDate : new Date(endDate);
    if (isMobile) return s.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ' - ' + e.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    return s.toLocaleDateString() + ' - ' + e.toLocaleDateString();
  };

const formatDateForUrl = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

const navigateToConversions = (row) => {
    const params = new URLSearchParams();
    params.set('startDate', formatDateForUrl(startDate));
    params.set('endDate', formatDateForUrl(endDate));
    params.set('status', 'approved');
    if (row.campaign_id) params.set('campaigns', row.campaign_id);
    if (row.partner_id) params.set('partners', row.partner_id);
    if (row.advertiser_id) params.set('advertisers', row.advertiser_id);
    else if (filters.advertisers?.length > 0) params.set('advertisers', filters.advertisers.join(','));
    window.open(`/conversions?${params.toString()}`, '_blank');
  };

  const filteredCampaigns = campaigns.filter(c => c.name?.toLowerCase().includes(searchCampaign.toLowerCase()) && (searchCampaign || c.status === 'active')).sort((a, b) => (b.status === 'active') - (a.status === 'active'));
  const filteredPartners = partners.filter(p => p.name?.toLowerCase().includes(searchPartner.toLowerCase()) && (searchPartner || p.status === 'active')).sort((a, b) => (b.status === 'active') - (a.status === 'active'));
  const filteredAdvertisers = advertisers.filter(a => a.name?.toLowerCase().includes(searchAdvertiser.toLowerCase()) && (searchAdvertiser || a.status === 'active')).sort((a, b) => (b.status === 'active') - (a.status === 'active'));

  const visibleGroupingColumns = isPartnerRole 
    ? groupingColumns.map(g => ({ ...g, columns: g.columns.filter(c => c !== 'partner' && c !== 'advertiser') }))
    : groupingColumns;

  const visibleMetrics = isPartnerRole 
    ? metricsColumns.filter(m => !['revenue', 'profit', 'margin'].includes(m))
    : metricsColumns;

  const displayMetrics = isMobile 
    ? visibleMetrics.filter(m => ['clicks', 'total_cv', 'revenue', 'payout'].includes(m))
    : visibleMetrics;

  const labels = isMobile ? columnLabelsShort : columnLabels;

  const renderCellValue = (col, row) => {
    const value = row[col];
    if (!value && value !== 0) return '-';
    
    if (col === 'campaign' && row.campaign_id) {
      return (
        <Link 
          component="button" 
          variant="body2" 
          onClick={() => navigate(`/campaigns/${row.campaign_id}`)}
          sx={{ textAlign: 'left', fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {value}
        </Link>
      );
    }
    if (col === 'partner' && row.partner_id) {
      return (
        <Link 
          component="button" 
          variant="body2" 
          onClick={() => navigate(`/partners/${row.partner_id}`)}
          sx={{ textAlign: 'left', fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {value}
        </Link>
      );
    }
    if (col === 'advertiser' && row.advertiser_id) {
      return (
        <Link 
          component="button" 
          variant="body2" 
          onClick={() => navigate(`/advertisers/${row.advertiser_id}`)}
          sx={{ textAlign: 'left', fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {value}
        </Link>
      );
    }
    return value;
  };

  const renderMetricValue = (col, row) => {
    const value = row[col];
    const formatted = fmt(col, value);
    
    if ((col === 'total_cv' || col === 'events') && value > 0) {
      return (
        <Link 
          component="button" 
          variant="body2" 
          onClick={() => navigateToConversions(row)}
          sx={{ fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {formatted}
        </Link>
      );
    }
    return formatted;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" fontWeight={700} mb={3} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Analytics</Typography>
        
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outlined" startIcon={<CalendarIcon />} onClick={(e) => setDateAnchor(e.currentTarget)} size={isMobile ? 'small' : 'medium'} sx={{ minWidth: { xs: 'auto', sm: 180 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {formatDateRange()}
              </Button>
              <Popover open={Boolean(dateAnchor)} anchorEl={dateAnchor} onClose={() => setDateAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box sx={{ p: 2, minWidth: 200 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Presets</Typography>
                  <List dense>{datePresets.map(p => <ListItemButton key={p.label} onClick={() => applyPreset(p)}>{p.label}</ListItemButton>)}</List>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <DatePicker label="Start" value={startDate} onChange={(date) => setStartDate(date)} slotProps={{ textField: { size: 'small' } }} />
                    <DatePicker label="End" value={endDate} onChange={(date) => setEndDate(date)} slotProps={{ textField: { size: 'small' } }} />
                  </Box>
                </Box>
              </Popover>

              <Button variant="outlined" startIcon={<ColumnsIcon />} onClick={(e) => setColAnchor(e.currentTarget)} size={isMobile ? 'small' : 'medium'} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Group By
              </Button>
              <Popover open={Boolean(colAnchor)} anchorEl={colAnchor} onClose={() => setColAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box sx={{ p: 2, minWidth: 250 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Group results by:</Typography>
                  {visibleGroupingColumns.map(g => (
                    <Box key={g.group} mb={2}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{g.group}</Typography>
                      {g.columns.map(col => (
                        <ListItemButton key={col} onClick={() => toggleGrouping(col)} dense>
                          <Checkbox checked={selectedGrouping.includes(col)} size="small" />
                          <ListItemText primary={columnLabels[col]} />
                        </ListItemButton>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Popover>

              {!isMobile && (
                <>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Campaign</InputLabel>
                    <Select multiple value={filters.campaigns} onChange={(e) => setFilters({...filters, campaigns: e.target.value})} input={<OutlinedInput label="Campaign" />} renderValue={(sel) => sel.length + ' sel.'} MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}>
                      <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                        <TextField size="small" fullWidth placeholder="Search..." value={searchCampaign} onChange={(e) => setSearchCampaign(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                      </Box>
                      {filteredCampaigns.map(c => <MenuItem key={c.campaign_id} value={c.campaign_id}><Checkbox checked={filters.campaigns.includes(c.campaign_id)} /><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.status === 'active' ? 'success.main' : 'grey.400', mr: 1 }} /><ListItemText primary={`${c.name} (${c.campaign_id})`} /></MenuItem>)}
                    </Select>
                  </FormControl>
                  {!isPartnerRole && (
                    <>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Partner</InputLabel>
                        <Select multiple value={filters.partners} onChange={(e) => setFilters({...filters, partners: e.target.value})} input={<OutlinedInput label="Partner" />} renderValue={(sel) => sel.length + ' sel.'} MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}>
                          <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                            <TextField size="small" fullWidth placeholder="Search..." value={searchPartner} onChange={(e) => setSearchPartner(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                          </Box>
                          {filteredPartners.map(p => <MenuItem key={p.partner_id} value={p.partner_id}><Checkbox checked={filters.partners.includes(p.partner_id)} /><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.status === 'active' ? 'success.main' : 'grey.400', mr: 1 }} /><ListItemText primary={`${p.name} (${p.partner_id})`} /></MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Advertiser</InputLabel>
                        <Select multiple value={filters.advertisers} onChange={(e) => setFilters({...filters, advertisers: e.target.value})} input={<OutlinedInput label="Advertiser" />} renderValue={(sel) => sel.length + ' sel.'} MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}>
                          <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                            <TextField size="small" fullWidth placeholder="Search..." value={searchAdvertiser} onChange={(e) => setSearchAdvertiser(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                          </Box>
                          {filteredAdvertisers.map(a => <MenuItem key={a.advertiser_id} value={a.advertiser_id}><Checkbox checked={filters.advertisers.includes(a.advertiser_id)} /><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: a.status === 'active' ? 'success.main' : 'grey.400', mr: 1 }} /><ListItemText primary={`${a.name} (${a.advertiser_id})`} /></MenuItem>)}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </>
              )}

              <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadReport} size={isMobile ? 'small' : 'medium'} sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
                Run Report
              </Button>
            </Box>
          </CardContent>
        </Card>

        {selectedGrouping.length > 0 && !isMobile && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Grouped by:</Typography>
            {selectedGrouping.map(col => (<Chip key={col} label={columnLabels[col]} onDelete={() => toggleGrouping(col)} color="primary" variant="outlined" size="small" />))}
          </Box>
        )}

        {Object.keys(summary).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>Summary</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(8, 1fr)' }, gap: { xs: 1, sm: 2 } }}>
                {displayMetrics.slice(0, 8).map(m => (
                  <Box key={m} textAlign="center">
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>{labels[m]}</Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>{fmt(m, summary[m])}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        <Card>
          <TableContainer sx={{ maxHeight: { xs: 400, sm: 600 } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {selectedGrouping.map(c => (
                    <TableCell key={c} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 600 }}>
                      <TableSortLabel active={orderBy === c} direction={orderBy === c ? orderDir : 'asc'} onClick={() => handleSort(c)}>
                        {labels[c]}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  {displayMetrics.map(m => (
                    <TableCell key={m} align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 600 }}>
                      <TableSortLabel active={orderBy === m} direction={orderBy === m ? orderDir : 'asc'} onClick={() => handleSort(m)}>
                        {labels[m]}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={50} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                ) : data.length === 0 ? (
                  <TableRow><TableCell colSpan={50} align="center" sx={{ py: 4, color: 'text.secondary' }}>Click "Run Report" to load data</TableCell></TableRow>
                ) : sortedData.map((row, i) => (
                  <TableRow key={i} hover>
                    {selectedGrouping.map(c => (
                      <TableCell key={c} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                        {renderCellValue(c, row)}
                      </TableCell>
                    ))}
                    {displayMetrics.map(m => (
                      <TableCell key={m} align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{renderMetricValue(m, row)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </LocalizationProvider>
  );
}

export default Analytics;
